'use client';

import { c as _c } from "react/compiler-runtime";
import { Monitor, Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { type CSSProperties, type MouseEvent, useMemo } from 'react';
import { useIsClient } from '@/hooks/useIsClient';
import { startThemeTransition, type ThemeTransitionContext } from '@/lib/theme/theme-transition';
import { cn } from '@/lib/utils';
const THEME_OPTIONS = [{
  key: 'system',
  icon: Monitor,
  label: 'System default'
}, {
  key: 'light',
  icon: Sun,
  label: 'Light theme'
}, {
  key: 'dark',
  icon: Moon,
  label: 'Dark theme'
}] as const;
export type ThemeSliderKey = (typeof THEME_OPTIONS)[number]['key'];
const THEME_OPTION_COUNT = THEME_OPTIONS.length;
type ThemeSliderProperties = {
  className?: string;
  applyTheme?: (theme: ThemeSliderKey, context?: ThemeTransitionContext) => void;
};
const ACTIVE_ITEM_WIDTH_PX = 28;
const GAP_PX = 6;
const CONTAINER_PADDING_PX = 6;
const INDICATOR_SIZE_PX = 28;
const INDICATOR_OVERHANG_PX = (INDICATOR_SIZE_PX - ACTIVE_ITEM_WIDTH_PX) / 2;
const INDICATOR_BASE_OFFSET_PX = CONTAINER_PADDING_PX - INDICATOR_OVERHANG_PX;
const toPx = (value: number): string => `${String(value)}px`;
export function ThemeSlider(t0) {
  const $ = _c(35);
  const {
    className,
    applyTheme
  } = t0;
  const {
    theme,
    resolvedTheme,
    setTheme
  } = useTheme();
  const isClient = useIsClient();
  let t1;
  if ($[0] !== theme) {
    t1 = THEME_OPTIONS.findIndex(option => option.key === theme);
    $[0] = theme;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const index = t1;
  const activeIndex = index === -1 ? 0 : index;
  const effectiveTheme = resolvedTheme ?? theme;
  const isDarkMode = effectiveTheme === "dark";
  const indicatorOffset = activeIndex * (ACTIVE_ITEM_WIDTH_PX + GAP_PX);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = toPx(INDICATOR_BASE_OFFSET_PX);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== indicatorOffset) {
    t3 = toPx(indicatorOffset);
    $[3] = indicatorOffset;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = {
      left: t2,
      "--tw-translate-x": t3
    };
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const indicatorStyle = t4;
  const t5 = String(THEME_OPTION_COUNT);
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = toPx(ACTIVE_ITEM_WIDTH_PX);
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = toPx(GAP_PX);
    t8 = toPx(CONTAINER_PADDING_PX);
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      gridTemplateColumns: `repeat(${t5}, ${t6})`,
      columnGap: t7,
      padding: `0 ${t8}`,
      width: toPx(THEME_OPTION_COUNT * ACTIVE_ITEM_WIDTH_PX + (THEME_OPTION_COUNT - 1) * GAP_PX + CONTAINER_PADDING_PX * 2)
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const sliderStyle = t9;
  if (!isClient) {
    let t10;
    if ($[11] !== className) {
      t10 = cn("relative m-0 border-0 p-0", className);
      $[11] = className;
      $[12] = t10;
    } else {
      t10 = $[12];
    }
    let t11;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <div className="relative grid h-8 overflow-hidden rounded-full border border-border/60" style={sliderStyle}><div className="pointer-events-none absolute top-1/2 h-[28px] w-[28px] -translate-y-1/2 transform rounded-full bg-foreground/5" style={{
          left: toPx(INDICATOR_BASE_OFFSET_PX)
        }} /></div>;
      $[13] = t11;
    } else {
      t11 = $[13];
    }
    let t12;
    if ($[14] !== t10) {
      t12 = <fieldset className={t10} aria-label="Theme selector">{t11}</fieldset>;
      $[14] = t10;
      $[15] = t12;
    } else {
      t12 = $[15];
    }
    return t12;
  }
  let t10;
  if ($[16] !== className) {
    t10 = cn("relative m-0 border-0 p-0", className);
    $[16] = className;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = cn("relative grid h-8 grid-cols-3 place-items-center overflow-hidden rounded-full border border-border/60 bg-transparent text-muted-foreground transition-colors", "dark:border-border/40 dark:text-muted-foreground");
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = isDarkMode ? "bg-card/60 ring-border/40" : "bg-card ring-border/40";
  let t13;
  if ($[19] !== t12) {
    t13 = cn("pointer-events-none absolute top-1/2 z-layer-surface flex h-[28px] w-[28px] -translate-y-1/2 transform items-center justify-center rounded-full shadow-sm ring-1 transition-transform duration-200 ease-out", t12);
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== indicatorStyle || $[22] !== t13) {
    t14 = <div className={t13} style={indicatorStyle} />;
    $[21] = indicatorStyle;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== activeIndex || $[25] !== applyTheme || $[26] !== setTheme || $[27] !== theme) {
    t15 = THEME_OPTIONS.map((option_0, index_0) => {
      const Icon = option_0.icon;
      const isActive = activeIndex === index_0;
      const handleClick = event => {
        if (isActive) {
          return;
        }
        const context = {
          element: event.currentTarget,
          pointerClientX: event.clientX,
          pointerClientY: event.clientY
        };
        if (applyTheme) {
          applyTheme(option_0.key, context);
          return;
        }
        startThemeTransition({
          nextTheme: option_0.key,
          setTheme,
          context,
          currentTheme: theme ?? null
        });
      };
      return <button key={option_0.key} type="button" onClick={handleClick} className={cn("relative z-layer-raised flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent", "hover:bg-muted/20 hover:text-foreground dark:hover:bg-muted/30", isActive && "text-foreground")} aria-label={option_0.label} data-testid={`theme-${option_0.key}`} aria-pressed={isActive}><Icon className="h-4 w-4" strokeWidth={1.75} /></button>;
    });
    $[24] = activeIndex;
    $[25] = applyTheme;
    $[26] = setTheme;
    $[27] = theme;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t14 || $[30] !== t15) {
    t16 = <div className={t11} style={sliderStyle}>{t14}{t15}</div>;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t10 || $[33] !== t16) {
    t17 = <fieldset className={t10} aria-label="Theme selector">{t16}</fieldset>;
    $[32] = t10;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  return t17;
}